<template>
  <span class="actuals-help-wrap">
    <b-button
      size="sm"
      variant="success"
      title="About booking actuals and the troubleshooter"
      aria-label="Help"
      @click="show_help = true"
    >
      <i class="fa fa-info-circle"></i>
    </b-button>

    <b-modal
      v-model="show_help"
      title="Booking Actuals & Troubleshooter"
      size="lg"
      ok-only
      scrollable
    >
      <div class="actuals-help-body">
        <h4>How booking actuals processing works</h4>
        <p>{{ processing_intro }}</p>
        <p>
          When a match succeeds, the system calculates actual start and finish times, distance, and duration
          for payroll and reporting. Processing runs automatically on a schedule; you can also reprocess
          individual {{ row_label_plural }} from the Actuals page when that option is available.
        </p>

        <hr/>

        <h4>Your actuals processing rules</h4>
        <p v-if="rules_loading">Loading rules for {{ tenant_name }}...</p>
        <p v-else-if="rules_error" class="actuals-help-error">{{ rules_error }}</p>
        <div v-else-if="rules_summary">
          <p>
            <b>{{ rules_summary.processor_label }}</b>
            <span v-if="rules_summary.processor_description"> — {{ rules_summary.processor_description }}</span>
            <span v-if="rules_summary.telematics_type"> Telematics: {{ rules_summary.telematics_type }}.</span>
          </p>
          <div
            v-for="section in rules_summary.sections"
            :key="section.title"
            class="actuals-help-rules-section"
          >
            <h5>{{ section.title }}</h5>
            <ul>
              <li v-for="(item, idx) in section.items" :key="idx">{{ item }}</li>
            </ul>
          </div>
        </div>

        <hr/>

        <h4>What each status means</h4>
        <table class="actuals-help-status-table">
          <tr>
            <td class="actuals-help-status success">Success</td>
            <td>The {{ row_label }} was matched to telematics. The status message shows calculated start/finish times, distance, and duration.</td>
          </tr>
          <tr>
            <td class="actuals-help-status error">Error</td>
            <td>The system tried to process the {{ row_label }} but could not complete the match. The status message lists specific problems — hover or click the red warning icon for a categorized explanation and suggested fix.</td>
          </tr>
          <tr>
            <td class="actuals-help-status unprocessed">Unprocessed</td>
            <td>The {{ row_label }} has not been processed yet, or no telematics data was available when processing ran. Normal for future trips; past trips may still be waiting on telematics delivery (allow 24–48 hours).</td>
          </tr>
          <tr>
            <td class="actuals-help-status skipped">Skipped</td>
            <td>{{ skipped_status_help }}</td>
          </tr>
          <tr>
            <td class="actuals-help-status cancelled">Cancelled</td>
            <td>{{ cancelled_status_help }}</td>
          </tr>
        </table>

        <hr/>

        <div>
          <h4>Using actuals feedback</h4>
          <p>
            You can flag a concern on each processed {{ row_label }} for your team to follow up
            on — support is notified automatically to help investigate. Expand a row with <b>+</b>
            to see the feedback controls below the status message{{ hos_feedback_note }}.
          </p>
          <ul>
            <li><b>Flag</b> — record a concern. Enter a short comment describing the issue, then submit. Click the flag again to edit or clear the concern and comment.</li>
          </ul>
          <template v-if="feedback_audit_mode_enabled">
            <p>Feedback audit mode is enabled for your organization, which adds:</p>
            <ul>
              <li><b>Thumbs up</b> — mark the actual as acceptable. Click again to clear your rating.</li>
              <li><b>Thumbs down</b> — mark the actual as incorrect or not usable. Click again to clear.</li>
              <li>Use the <b>Feedback</b> filter chips on the Actuals page to show only rows with a given rating. Hover each chip for a short description. Click a chip to filter; click the same chip again to show all rows.</li>
              <li>The <b>&times;</b> button on the Feedback filters clears every rating and comment for all actuals in the current date range (you will be asked to confirm).</li>
            </ul>
          </template>
          <hr/>
        </div>

        <h4>Using the {{ actuals_page }} page</h4>
        <ul>
          <li>Select a date range and click <b>Search</b> to load {{ row_label_plural }} scheduled to finish in that window.</li>
          <li>Click the colored summary chips (<b>Success</b>, <b>Error</b>, <b>Unprocessed</b>, <b>Skipped</b>, <b>Cancelled</b>) to filter the table by status. Skipped {{ row_label_plural }} are hidden by default — click <b>Skipped</b> to show them.</li>
          <li>Click <b>+</b> on a row to expand {{ row_label }} details, including telematics sources used for the calculation.</li>
          <li>On errored rows, hover or click the red <i class="fa fa-exclamation-triangle"></i> icon next to the status message for a quick explanation of that error.</li>
          <li>Click <b>Troubleshoot Errors</b> to open the troubleshooter for the same date range and see all errors grouped by root cause.</li>
        </ul>

        <hr/>

        <h4>Using the Actuals Troubleshooter</h4>
        <p>
          The troubleshooter summarizes errors across a date range so you can spot patterns and fix root causes
          instead of investigating {{ row_label_plural }} one at a time.
        </p>
        <ul>
          <li>Adjust the date range and click <b>Get Results</b>, or use the arrow buttons to shift the range day by day.</li>
          <li>Summary chips at the top use the same date range and filters as the Actuals page, so Success, Error, Unprocessed, Cancelled, and Skipped counts should match.</li>
          <li>Errors are grouped into three areas:
            <b>Booking data issues</b> (fix in {{ booking_system }}),
            <b>Telematics matching issues</b> (vehicle/driver names, GPS, HOS),
            and <b>Other processing issues</b>.
            Booking data issues include overlapping scheduled times — when the same driver or vehicle
            is assigned to two bookings whose planned windows overlap, actuals are blocked until the
            schedule is corrected.
          </li>
          <li>Category counts and percentages are based on distinct Actuals page result rows with at least one matching error, not individual error messages. A row with multiple error messages is counted once per category. Percentages are the share of the total errors at the top.</li>
          <li>Click a category header to expand it and see affected {{ booking_label_plural }}. {{ id_label }}s link back to the Actuals page.</li>
          <li>Each category includes <b>What it means</b> and a <b>Suggested fix</b> for the most common resolution.</li>
          <li>Summary tables at the bottom show errors by {{ booking_type_label }}, vehicle type, day, and the vehicles with the most errors. Unmatched vehicle IDs highlight fleet codes that do not exist in telematics.</li>
        </ul>
      </div>
    </b-modal>
  </span>
</template>

<script>
import { mapState } from 'vuex'
import { get_actuals_processing_rules } from '../networking/apis'
import { get_tbn_actuals_processing_rules } from '../networking/tbn_apis'
import { isFeedbackAuditModeEnabled } from '@/helpers/feedback_audit_mode'

export default {
  name: 'actuals_troubleshooter_help',

  props: {
    source: {
      type: String,
      default: 'cm',
      validator: value => ['cm', 'tbn', 'fasttrak'].includes(value),
    },
  },

  data() {
    return {
      show_help: false,
      rules_summary: null,
      rules_loading: false,
      rules_error: null,
    }
  },

  watch: {
    show_help(isOpen) {
      if (isOpen)
        this.load_processing_rules()
    },
  },

  computed: {
    ...mapState({
      user: state => state.profile.user,
    }),
    tenant_name() {
      return (this.user && this.user.tenant_name) || 'this tenant'
    },
    booking_system() {
      const names = { cm: 'Coach Manager', tbn: 'TBN', fasttrak: 'FastTrak' }
      return names[this.source] || 'your booking system'
    },
    actuals_page() {
      const names = { cm: 'Booking Actuals', tbn: 'Booking Actuals', fasttrak: 'Bookings & Actuals' }
      return names[this.source] || 'Booking Actuals'
    },
    row_label() {
      const labels = { cm: 'movement', tbn: 'dispatch', fasttrak: 'reservation' }
      return labels[this.source] || 'row'
    },
    row_label_plural() {
      const labels = { cm: 'movements', tbn: 'dispatches', fasttrak: 'reservations' }
      return labels[this.source] || 'rows'
    },
    booking_label_plural() {
      const labels = { cm: 'charters and contracts', tbn: 'charters and scheduled services', fasttrak: 'reservations' }
      return labels[this.source] || 'bookings'
    },
    id_label() {
      const labels = { cm: 'Charter/contract ID', tbn: 'Booking ID', fasttrak: 'Reservation ID' }
      return labels[this.source] || 'Booking ID'
    },
    booking_type_label() {
      const labels = { cm: 'charter vs. contract', tbn: 'charter vs. scheduled service', fasttrak: 'booking type' }
      return labels[this.source] || 'booking type'
    },
    processing_intro() {
      const telematics = 'vehicle GPS locations, driver hours-of-service (HOS) logins/logouts, and DVIR inspections when available'
      const intros = {
        cm: `In Coach Manager, planned data is imported as charters (private hires) or contracts. Each booking has one or more movements. After a trip runs, the actuals pipeline matches each movement to telematics data — ${telematics}.`,
        tbn: `In TBN, planned data is imported as charters or scheduled services. Each booking has one or more routes, and each route has one or more dispatches. After a trip runs, the actuals pipeline matches each dispatch to telematics data — ${telematics}.`,
        fasttrak: `In FastTrak, planned data is imported as reservations, each with a single driver and vehicle assignment. After a trip runs, the actuals pipeline matches each reservation to telematics data — ${telematics}.`,
      }
      return intros[this.source] || intros.cm
    },
    cancelled_status_help() {
      const help = {
        cm: 'The charter, contract, or movement was cancelled in Coach Manager. Cancelled movements are excluded from most reports.',
        tbn: 'The charter, scheduled service, route, or dispatch was cancelled in TBN. Cancelled dispatches are excluded from most reports.',
        fasttrak: 'The reservation was cancelled in FastTrak. Cancelled reservations are excluded from most reports.',
      }
      return help[this.source] || help.cm
    },
    skipped_status_help() {
      const help = {
        cm: 'The movement was intentionally excluded from actuals processing — usually because its vehicle type is not in your Supported Actuals Vehicle Types setting, or the assigned vehicle is not expected to match telematics. Skipped rows are not errors.',
        tbn: 'The dispatch was intentionally excluded from actuals processing — usually because its vehicle type is not in your Supported Actuals Vehicle Types setting, or the assigned vehicle could not be matched in telematics. Skipped rows are not errors.',
        fasttrak: 'The reservation was intentionally excluded from actuals processing — usually because its vehicle type is not in your Supported Actuals Vehicle Types setting. Skipped rows are not errors.',
      }
      return help[this.source] || help.cm
    },
    troubleshooter_totals_help() {
      const help = {
        cm: 'the same row count as the Actuals page, plus successes, errors, unprocessed, cancelled, and skipped counts',
        tbn: 'the same row count as the Actuals page, plus successes, errors, unprocessed, cancelled, and skipped counts',
        fasttrak: 'reservations, plus successes, errors, unprocessed, cancelled, and skipped counts',
      }
      return help[this.source] || help.cm
    },
    feedback_audit_mode_enabled() {
      return isFeedbackAuditModeEnabled(this.user)
    },
    hos_feedback_note() {
      return this.source === 'tbn'
        ? ', or open the HOS timeline modal for the same controls'
        : ''
    },
  },

  methods: {
    async load_processing_rules() {
      if (!this.user || !this.user.tenant || this.rules_loading)
        return

      this.rules_loading = true
      this.rules_error = null

      try {
        const fetchRules = this.source === 'tbn'
          ? get_tbn_actuals_processing_rules
          : get_actuals_processing_rules
        const { value: { data = null } = {} } = await fetchRules(this.user.tenant)
        this.rules_summary = data
        if (!data)
          this.rules_error = 'Unable to load actuals processing rules for this tenant.'
      } catch (error) {
        this.rules_summary = null
        this.rules_error = 'Unable to load actuals processing rules for this tenant.'
      } finally {
        this.rules_loading = false
      }
    },
  },
}
</script>

<style scoped>
.actuals-help-wrap {
  display: inline-block;
  margin-left: 8px;
}
.actuals-help-body {
  font-size: 15px;
  line-height: 1.5;
  text-align: left;
}
.actuals-help-body h4 {
  font-size: 17px;
  font-weight: bold;
  margin-bottom: 8px;
}
.actuals-help-body p {
  margin-bottom: 10px;
}
.actuals-help-body ul {
  padding-left: 20px;
  margin-bottom: 0;
}
.actuals-help-body li {
  margin-bottom: 6px;
}
.actuals-help-rules-section {
  margin-bottom: 12px;
}
.actuals-help-rules-section h5 {
  font-size: 15px;
  font-weight: bold;
  margin-bottom: 4px;
}
.actuals-help-error {
  color: #c0392b;
}
.actuals-help-status-table {
  width: 100%;
  border-collapse: collapse;
}
.actuals-help-status-table td {
  border: 1px solid #ddd;
  padding: 8px 10px;
  vertical-align: top;
}
.actuals-help-status {
  font-weight: bold;
  white-space: nowrap;
  width: 110px;
}
.actuals-help-status.success { background-color: hsla(120, 100%, 75%, 0.35); }
.actuals-help-status.error { background-color: hsla(0, 100%, 75%, 0.35); }
.actuals-help-status.unprocessed { background-color: hsla(200, 40%, 80%, 0.35); }
.actuals-help-status.skipped { background-color: hsla(190, 90%, 80%, 0.35); }
.actuals-help-status.cancelled { background-color: hsla(300, 60%, 80%, 0.35); }
</style>
